import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Grid,
} from "@mui/material";

import {
  getMaterialityIndustries,
  getOffsetCountries,
  getProgramTypes,
  getSupplierSurveyResults,
} from "../../../redux/actions";
import { STATUS } from "../../../redux/constants";

import useStyles from "./styles";
import CeroAutoComplete from "../../../components/CeroAutoComplete";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { sampleYear } from "../../../constants";
import SurveyResultTable from "./resultTable";

const SurveyResult = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [selectedYear, setSelectedYear] = useState(0);
  const [industryType, setIndustryType] = useState(null);
  const [sortDataAscending, setSortDataAscending] = useState(false);
  const [sortDataDescending, setSortDataDescending] = useState(false);

  const industryData =
    useSelector((state) => state.materiality.getMaterialityIndustries.data) ||
    [];
  const surveyResults =
    useSelector(
      (state) => state.supplierScreening.getSupplierSurveyResults.data
    ) || [];
  const surveyResultsStatus =
    useSelector(
      (state) => state.supplierScreening.getSupplierSurveyResults.status
    ) || [];

  const surveyResultColumns = [
    {
      columnKey: "industry_name",
      columnId: "industry_name",
      columnHeader: "Industry Type",
      width: "25vw",
    },
    {
      columnKey: "company_name",
      columnId: "company_name",
      columnHeader: "Company Name",
      width: "25vw",
    },
    {
      columnKey: "survey_date",
      columnId: "survey_date",
      columnHeader: "date",
      width: "25vw",
    },
    {
      columnKey: "total_score",
      columnId: "total_score",
      columnHeader: "Score",
      width: "25vw",
      classes: { column: classes.scorecolumn },
    },
    {
      columnKey: "assessment",
      columnId: "assessment",
      columnHeader: "Assessment Rating",
      width: "25vw",
    },
  ];

  const updatedData = () => {
    if (!surveyResults) return [];

    const tempData = surveyResults.map((item) => ({
      ...item,
      assessment: (() => {
        if (item.total_score >= 90 && item.total_score <= 100) {
          return (
            <Typography className={classes.excellent}>
              Excellent commitment
            </Typography>
          );
        }
        if (item.total_score >= 75 && item.total_score < 90) {
          return (
            <Typography className={classes.good}>Good commitment</Typography>
          );
        }
        if (item.total_score >= 50 && item.total_score < 75) {
          return (
            <Typography className={classes.moderate}>
              Moderate commitment
            </Typography>
          );
        }
        return <Typography className={classes.low}>Low commitment</Typography>;
      })(),
    }));

    // Sorting logic
    if (sortDataAscending) {
      return tempData.sort((a, b) =>
        a.industry_name.localeCompare(b.industry_name)
      );
    }
    if (sortDataDescending) {
      return tempData.sort((a, b) =>
        b.industry_name.localeCompare(a.industry_name)
      );
    }

    return tempData; // Return the unsorted data if no sorting is applied
  };

  const yearList = sampleYear().map((item) => ({
    id: item.key,
    label: item.value,
  }));

  const industryList = Array.isArray(industryData)
    ? industryData.map((item) => ({
        id: item.id,
        label: item.industry_type,
      }))
    : [];

  useEffect(() => {
    dispatch(getMaterialityIndustries());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      getSupplierSurveyResults(selectedYear || 0, industryType?.id || "")
    );
  }, [dispatch, selectedYear, industryType]);

  useEffect(() => {
    dispatch(getOffsetCountries());
    dispatch(getProgramTypes());
  }, [dispatch, selectedYear, industryType]);

  return (
    <DashboardLayout>
      <Container className={classes.container}>
        <div className={classes.topContainer}>
          <Grid spacing={2} className={classes.filterContainer}>
            <Grid item style={{ width: "90%" }}>
              <CeroAutoComplete
                classes={{
                  container: classes.autoComplete,
                  root: classes.textField,
                }}
                id="year"
                label="Year"
                fullWidth
                selectedValue={selectedYear}
                onChange={(e, value) =>
                  setSelectedYear(value ? value.id : undefined)
                }
                options={yearList}
              />
            </Grid>
            <Grid item style={{ width: "90%" }}>
              <CeroAutoComplete
                classes={{
                  container: classes.autoComplete,
                  root: classes.textField,
                }}
                id={`industry-type`}
                label="Industry Type"
                fullWidth
                value={
                  industryList.find((item) => item.id === industryType)
                    ?.label || ""
                }
                onChange={(e, newValue) => setIndustryType(newValue)}
                options={industryList || []}
              />
            </Grid>
          </Grid>
        </div>
        {surveyResultsStatus === STATUS.SUCCESS ? (
          <Container className={classes.tableContainer}>
            <SurveyResultTable
              columns={surveyResultColumns || []}
              data={updatedData()}
              hasMore={false}
              loading={false}
              loadMore={false}
              classes={{ tableContainer: classes.tableContainer }}
              showSelectAllCheckbox={false}
              isSelectable
              showDropdown={true}
              checkedRows={[]}
              onCheckedRowsChange={() => console.log("hello")}
              showIcon={!false}
              showFilterIcon={true}
              onFilterSelect={() => console.log("hello")}
              setSortDataAscending={setSortDataAscending}
              setSortDataDescending={setSortDataDescending}
              sortDataAscending={sortDataAscending}
              sortDataDescending={sortDataDescending}
              showSort={true}
            />
          </Container>
        ) : (
          <Box className={classes.loader}>
            <Typography variant="h7" component="span">
              {surveyResultsStatus === STATUS.RUNNING ? (
                <CircularProgress />
              ) : surveyResultsStatus === STATUS.ERROR ? (
                "We couldn't process your request. Please try again later"
              ) : (
                ""
              )}
            </Typography>
          </Box>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default SurveyResult;
